import type { Any } from '../../interface'
import type { AstNode, BindingTarget } from '../../parser/types'
import type { NodeTypes } from '../../constants/constants'
import type { BuiltinSpecialExpression, CustomDocs } from '../interface'
import { getAllBindingTargetNames } from '../bindingNode'
import type { Context } from '../../evaluator/interface'

// Each case: [pattern, body, guard?]
export type MatchCase = [BindingTarget, AstNode, AstNode | undefined]
export type MatchNode = [typeof NodeTypes.Match, [AstNode, MatchCase[]], number]

const docs: CustomDocs = {
  category: 'special-expression',
  customVariants: ['match value match-branch match-branch ... end'],
  details: [
    ['value', 'any', 'The value to match against patterns.'],
    ['match-branch', 'case pattern [when guard] then body', 'A branch of the match expression.'],
    ['pattern', 'pattern', 'A pattern to match: literal, variable, array destructuring, object destructuring, or wildcard (_).'],
    ['guard', 'expression', 'An optional guard expression that must be truthy for the match to succeed.'],
    ['body', 'expressions', 'The expressions to evaluate if the pattern matches.'],
  ],
  description: 'Pattern matching expression. Matches `value` against each `pattern` sequentially. If a pattern matches (and the optional `when` guard is truthy), the corresponding `body` is evaluated and its result returned. Bound variables from the pattern are available in the guard and body. If no pattern matches, `null` is returned.',
  examples: [
    `
let day = if true then 1 else 2 end;
match day
  case 1 then "One"
  case 2 then "Two"
end`,
    { code: `
match [1, 2, 3]
  case [x] then "one element"
  case [x, y] then "two elements"
  case [x, ...xs] then "first: " ++ str(x) ++ " rest: " ++ str(xs)
end`, noCheck: true },
    { code: `
match { type: "click", x: 10, y: 20 }
  case { type: "click", x, y } then "Click at " ++ str(x) ++ ", " ++ str(y)
  case { type: "keydown", key } then "Key: " ++ key
  case _ then "unknown event"
end`, noCheck: true },
    `
let user = if true then { role: "admin", name: "Alice" } else { role: "user", name: "Bob" } end;
match user
  case { role: "admin", name } then "Admin: " ++ name
  case { role, name } when role == "user" then "User: " ++ name
  case _ then "Unknown role"
end`,
  ],
}

export const matchSpecialExpression: BuiltinSpecialExpression<Any, MatchNode> = {
  arity: {},
  docs,
  getUndefinedSymbols: (node, contextStack, { getUndefinedSymbols, builtin }) => {
    const result = new Set<string>()

    // The match value expression
    const [matchValue, cases] = node[1]
    getUndefinedSymbols([matchValue], contextStack, builtin).forEach(s => result.add(s))

    // Each case
    for (const [pattern, body, guard] of cases) {
      const newContext: Context = {}
      Object.assign(newContext, getAllBindingTargetNames(pattern))
      const caseContextStack = contextStack.create(newContext)

      if (guard) {
        getUndefinedSymbols([guard], caseContextStack, builtin).forEach(s => result.add(s))
      }
      getUndefinedSymbols([body], caseContextStack, builtin).forEach(s => result.add(s))
    }

    return result
  },
}
